import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, Download, Calendar, Filter, Search, TrendingUp, Clock, FileBarChart, CheckCircle, AlertCircle, ExternalLink, Eye, Trash2, MoreVertical, TrendingDown, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
export function ReportsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('30');
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Mock data for reports
  const recentReports = [{
    id: 1,
    name: 'Monthly Analytics Report',
    type: 'Analytics',
    date: '2024-01-15',
    size: '2.4 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 12
  }, {
    id: 2,
    name: 'Learner Progress Summary',
    type: 'Progress',
    date: '2024-01-14',
    size: '1.8 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 8
  }, {
    id: 3,
    name: 'Skills Completion Report',
    type: 'Completion',
    date: '2024-01-13',
    size: '3.1 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 15
  }, {
    id: 4,
    name: 'Assessment Performance',
    type: 'Assessment',
    date: '2024-01-12',
    size: '2.7 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 10
  }, {
    id: 5,
    name: 'Quarterly Review Q4 2024',
    type: 'Analytics',
    date: '2024-01-10',
    size: '4.2 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 20
  }];
  const reportTemplates = [{
    id: 1,
    name: 'Learner Progress Report',
    description: 'Track individual learner progress across all skills',
    icon: Users,
    color: 'from-[#30718D] to-[#265a70]',
    fields: ['Learner Name', 'Skill', 'Date Range']
  }, {
    id: 2,
    name: 'Skills Analytics',
    description: 'Comprehensive analytics for specific skills',
    icon: BarChart3,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Skill', 'Date Range', 'Metrics']
  }, {
    id: 3,
    name: 'Assessment Results',
    description: 'Detailed assessment performance and scores',
    icon: FileBarChart,
    color: 'from-purple-500 to-purple-600',
    fields: ['Assessment', 'Date Range', 'Learner Group']
  }, {
    id: 4,
    name: 'Completion Summary',
    description: 'Overview of skill completions and certifications',
    icon: CheckCircle,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Date Range', 'Skill Category']
  }];
  const quickStats = [{
    label: 'Total Reports',
    value: '48',
    change: '+8',
    trend: 'up',
    icon: FileText
  }, {
    label: 'Generated This Month',
    value: '12',
    change: '+3',
    trend: 'up',
    icon: TrendingUp
  }, {
    label: 'Total Downloads',
    value: '156',
    change: '+24',
    trend: 'up',
    icon: Download
  }, {
    label: 'Scheduled Reports',
    value: '6',
    change: '0',
    trend: 'neutral',
    icon: Clock
  }];
  const filteredReports = recentReports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || report.type.toLowerCase() === selectedFilter;
    return matchesSearch && matchesFilter;
  });
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1713">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1714">
        <div className="max-w-[1440px] mx-auto px-8" data-id="element-1715">
          <div className="flex items-center h-16" data-id="element-1716">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-1717">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-8 ml-12" data-id="element-1718">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1719">
                <Home size={18} data-id="element-1720" />
                <span data-id="element-1721">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1722">
                <BarChart3 size={18} data-id="element-1723" />
                <span data-id="element-1724">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1725">
                <FileText size={18} data-id="element-1726" />
                <span data-id="element-1727">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1728">
                <UserCog size={18} data-id="element-1729" />
                <span data-id="element-1730">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-4 ml-auto" data-id="element-1731">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1732">
                <Bell size={20} data-id="element-1733" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1734"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1735">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1736">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1737">
                    LT
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1738">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1739" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1740">
                    <div className="py-2" data-id="element-1741">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1742">
                        <Users size={18} className="text-gray-600" data-id="element-1743" />
                        <span className="font-medium text-gray-900" data-id="element-1744">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1745">
                        <Home size={18} className="text-gray-600" data-id="element-1746" />
                        <span className="font-medium text-gray-900" data-id="element-1747">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1748">
                        <UserCog size={18} className="text-gray-600" data-id="element-1749" />
                        <span className="font-medium text-gray-900" data-id="element-1750">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1751">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1752">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-1753"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-1754"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1755">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1756"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1757">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1758">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1759"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1760"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1761"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1762">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-8 py-8" data-id="element-1763">
        {/* Header */}
        <div className="mb-8" data-id="element-1764">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1765">Reports</h1>
          <p className="text-gray-600" data-id="element-1766">
            Generate, download, and manage your learning platform reports
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8" data-id="element-1767">
          {quickStats.map((stat, index) => {
          const Icon = stat.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1768">
                <div className="flex items-center justify-between mb-2" data-id="element-1769">
                  <span className="text-sm font-medium text-gray-600" data-id="element-1770">
                    {stat.label}
                  </span>
                  {stat.trend === 'up' ? <TrendingUp size={20} className="text-green-600" data-id="element-1771" /> : stat.trend === 'down' ? <TrendingUp size={20} className="text-red-600 rotate-180" data-id="element-1772" /> : <Activity size={20} className="text-gray-400" data-id="element-1773" />}
                </div>
                <div className="flex items-end justify-between" data-id="element-1774">
                  <div className="text-3xl font-bold text-gray-900" data-id="element-1775">
                    {stat.value}
                  </div>
                  <span className={`text-lg font-bold ${stat.trend === 'up' ? 'text-green-600' : stat.trend === 'down' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1776">
                    {stat.change}
                  </span>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Report Templates */}
        <div className="mb-8" data-id="element-1777">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1778">
            Generate New Report
          </h2>
          <div className="grid grid-cols-2 gap-6" data-id="element-1779">
            {reportTemplates.map((template, index) => {
            const Icon = template.icon;
            return <motion.div key={template.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1780">
                  <div className="flex items-start gap-4" data-id="element-1781">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-1782">
                      <Icon size={24} className="text-white" data-id="element-1783" />
                    </div>
                    <div className="flex-1" data-id="element-1784">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#30718D] transition-colors" data-id="element-1785">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-1786">
                        {template.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4" data-id="element-1787">
                        {template.fields.map((field, idx) => <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium" data-id="element-1788">
                            {field}
                          </span>)}
                      </div>
                      <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] transition-colors flex items-center gap-1" data-id="element-1789">
                        Generate Report
                        <ExternalLink size={14} data-id="element-1790" />
                      </button>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Recent Reports */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-1791">
          <div className="p-6 border-b border-gray-200" data-id="element-1792">
            <div className="flex items-center justify-between mb-4" data-id="element-1793">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1794">
                Recent Reports
              </h2>
              <div className="flex items-center gap-3" data-id="element-1795">
                <select value={selectedTimeframe} onChange={e => setSelectedTimeframe(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-1796">
                  <option value="7" data-id="element-1797">Last 7 Days</option>
                  <option value="30" data-id="element-1798">Last 30 Days</option>
                  <option value="90" data-id="element-1799">Last 90 Days</option>
                  <option value="all" data-id="element-1800">All Time</option>
                </select>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center gap-4" data-id="element-1801">
              <div className="flex-1 relative" data-id="element-1802">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1803" />
                <input type="text" placeholder="Search reports..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm" data-id="element-1804" />
              </div>
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1" data-id="element-1805">
                <button onClick={() => setSelectedFilter('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1806">
                  All
                </button>
                <button onClick={() => setSelectedFilter('analytics')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'analytics' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1807">
                  Analytics
                </button>
                <button onClick={() => setSelectedFilter('progress')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'progress' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1808">
                  Progress
                </button>
                <button onClick={() => setSelectedFilter('assessment')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedFilter === 'assessment' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1809">
                  Assessment
                </button>
              </div>
            </div>
          </div>

          {/* Reports Table */}
          <div className="overflow-x-auto" data-id="element-1810">
            <table className="w-full" data-id="element-1811">
              <thead data-id="element-1812">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1813">
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-1814">
                    Report Name
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-1815">
                    Type
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-1816">
                    Date Generated
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-1817">
                    Size
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-1818">
                    Downloads
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-sm text-gray-700" data-id="element-1819">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1820">
                {filteredReports.map((report, index) => <motion.tr key={report.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-1821">
                    <td className="py-4 px-6" data-id="element-1822">
                      <div className="flex items-center gap-3" data-id="element-1823">
                        <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-1824">
                          <FileText size={18} className="text-[#30718D]" data-id="element-1825" />
                        </div>
                        <div data-id="element-1826">
                          <div className="font-medium text-gray-900" data-id="element-1827">
                            {report.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1828">
                            {report.format}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-1829">
                      <span className="px-2 py-1 bg-[#30718D]/10 text-[#30718D] rounded text-xs font-medium" data-id="element-1830">
                        {report.type}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600" data-id="element-1831">
                      {new Date(report.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600" data-id="element-1832">
                      {report.size}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600" data-id="element-1833">
                      {report.downloads}
                    </td>
                    <td className="py-4 px-6" data-id="element-1834">
                      <div className="flex items-center gap-2" data-id="element-1835">
                        <button className="p-2 text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-1836">
                          <Download size={16} data-id="element-1837" />
                        </button>
                        <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1838">
                          <Eye size={16} data-id="element-1839" />
                        </button>
                        <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1840">
                          <MoreVertical size={16} data-id="element-1841" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>

          {filteredReports.length === 0 && <div className="p-12 text-center" data-id="element-1842">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" data-id="element-1843" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1844">
                No reports found
              </h3>
              <p className="text-gray-600" data-id="element-1845">
                Try adjusting your search or filter criteria
              </p>
            </div>}
        </div>
      </div>
    </div>;
}